// StatusIndicator.jsx
// Enhanced status indicator with offline duration and animations
// Part of Device Management Overhaul - Phase 5: UI/UX Polish

import React, { memo, useMemo } from 'react';
import { Box, Typography, Chip, Tooltip, keyframes } from '@mui/material';
import {
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  Warning as WarningIcon,
  Schedule as ClockIcon,
  SignalWifiOff as DisconnectedIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

// Animation keyframes
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

const blink = keyframes`
  0%, 50%, 100% {
    opacity: 1;
  }
  25%, 75% {
    opacity: 0.5;
  }
`;

const StatusIndicator = memo(({ status, lastSeen, size = 'medium', showDuration = true, animate = true, device = null }) => {
  // Check if device is clearvox_user type - these don't have online/offline state
  // If it's in the list, it's configured - no status to show
  const deviceType = device?.deviceTypeModel?.type || device?.deviceTypeModel?.name || device?.deviceTypeModel?.friendlyName || '';
  const isClearvoxUser = deviceType.toLowerCase() === 'clearvox_user';

  // Calculate status details
  const statusDetails = useMemo(() => {
    if (isClearvoxUser) {
      return null; // No status for clearvox_user devices
    }

    // Ensure status is a string (handle cases where it might be an object)
    const statusString = typeof status === 'string' ? status : (status?.toString?.() || 'Unknown');
    const statusLower = statusString.toLowerCase();
    let color = 'default';
    let icon = <WarningIcon />;
    let label = statusString;
    let severity = 'warning';

    if (['registered', 'online', 'active'].includes(statusLower)) {
      color = 'success';
      icon = <OnlineIcon />;
      severity = 'success';
    } else if (['offline', 'inactive', 'disconnected'].includes(statusLower)) {
      color = 'error';
      icon = <OfflineIcon />;
      severity = 'error';
    } else if (statusLower === 'disconnected') {
      color = 'error';
      icon = <DisconnectedIcon />;
      severity = 'error';
    }

    return { color, icon, label, severity };
  }, [status, isClearvoxUser]);

  // Calculate offline duration - must be called unconditionally (React hooks rule)
  const offlineDuration = useMemo(() => {
    // Return null if no status details or device is online
    if (!statusDetails || !lastSeen || statusDetails.severity === 'success') return null;

    try {
      const lastSeenDate = new Date(lastSeen);
      const now = new Date();
      const hoursDiff = (now - lastSeenDate) / (1000 * 60 * 60);

      // Format based on duration
      if (hoursDiff < 1) {
        const minutes = Math.floor(hoursDiff * 60);
        return `${minutes}m`;
      } else if (hoursDiff < 24) {
        const hours = Math.floor(hoursDiff);
        const minutes = Math.floor((hoursDiff - hours) * 60);
        return `${hours}h ${minutes}m`;
      } else if (hoursDiff < 168) { // 7 days
        const days = Math.floor(hoursDiff / 24);
        const hours = Math.floor(hoursDiff % 24);
        return `${days}d ${hours}h`;
      } else {
        return formatDistanceToNow(lastSeenDate, { addSuffix: false });
      }
    } catch (e) {
      return null;
    }
  }, [lastSeen, statusDetails]);

  // Determine animation style - must be called unconditionally (React hooks rule)
  const animationStyle = useMemo(() => {
    if (!animate || !statusDetails) return {};

    if (statusDetails.severity === 'success') {
      return {
        animation: `${pulse} 2s infinite`
      };
    } else if (statusDetails.severity === 'error' && offlineDuration) {
      const hours = parseInt(offlineDuration);
      if (hours > 24) {
        return {
          animation: `${blink} 3s infinite`
        };
      }
    }

    return {};
  }, [animate, statusDetails, offlineDuration]);

  // Return null for clearvox_user devices (no status to show)
  // This must come AFTER all hooks to satisfy React's rules of hooks
  if (!statusDetails) {
    return null;
  }

  // Size configurations
  const sizeConfig = {
    small: { fontSize: '0.75rem', iconSize: 16, padding: '2px 8px' },
    medium: { fontSize: '0.875rem', iconSize: 20, padding: '4px 12px' },
    large: { fontSize: '1rem', iconSize: 24, padding: '6px 16px' }
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  const statusChip = (
    <Chip
      icon={React.cloneElement(statusDetails.icon, { sx: { fontSize: config.iconSize } })}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>{statusDetails.label}</span>
          {showDuration && offlineDuration && (
            <>
              <Typography
                component="span"
                sx={{
                  fontSize: config.fontSize,
                  opacity: 0.8,
                  ml: 0.5
                }}
              >
                •
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <ClockIcon sx={{ fontSize: config.iconSize * 0.8 }} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: config.fontSize,
                    fontWeight: 600
                  }}
                >
                  {offlineDuration}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      }
      color={statusDetails.color}
      size={size}
      variant="outlined"
      sx={{
        ...animationStyle,
        transition: 'all 0.3s ease',
        '& .MuiChip-label': {
          px: config.padding.split(' ')[1]
        }
      }}
    />
  );

  if (offlineDuration && showDuration) {
    const tooltipText = statusDetails.severity === 'error'
      ? `Device has been offline for ${offlineDuration}`
      : `Last seen ${offlineDuration} ago`;

    return (
      <Tooltip title={tooltipText} placement="top">
        {statusChip}
      </Tooltip>
    );
  }

  return statusChip;
});

StatusIndicator.displayName = 'StatusIndicator';

// Compact version for list views
export const CompactStatusIndicator = memo(({ status, lastSeen, device = null }) => {
  // Check if device is clearvox_user type - these don't have online/offline state
  // If it's in the list, it's configured - no status to show
  const deviceType = device?.deviceTypeModel?.type || device?.deviceTypeModel?.name || device?.deviceTypeModel?.friendlyName || '';
  const isClearvoxUser = deviceType.toLowerCase() === 'clearvox_user';

  if (isClearvoxUser) {
    return null; // No status for clearvox_user devices
  }

  // Ensure status is a string (handle cases where it might be an object)
  const statusString = typeof status === 'string' ? status : (status?.toString?.() || 'unknown');
  const statusLower = statusString.toLowerCase();
  const isOnline = ['registered', 'online', 'active'].includes(statusLower);

  const color = isOnline ? 'success.main' : 'error.main';
  const Icon = isOnline ? OnlineIcon : OfflineIcon;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Icon sx={{ fontSize: 16, color }} />
      {!isOnline && lastSeen && (
        <Typography variant="caption" color="text.secondary">
          {(() => {
            try {
              const hours = (new Date() - new Date(lastSeen)) / (1000 * 60 * 60);
              if (hours < 1) return `${Math.floor(hours * 60)}m`;
              if (hours < 24) return `${Math.floor(hours)}h`;
              return `${Math.floor(hours / 24)}d`;
            } catch (e) {
              return '';
            }
          })()}
        </Typography>
      )}
    </Box>
  );
});

CompactStatusIndicator.displayName = 'CompactStatusIndicator';

export default StatusIndicator;